import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Building2, Lock, ShieldCheck, ShieldAlert, CheckCircle2,
  AlertTriangle, Upload, Plus, Trash2, Phone, Mail, MapPin,
  Loader2, Check, Download, FileText, ArrowRight, RefreshCw, Send, Landmark
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const COMMON_VENDOR_KYC_DOCS = [
  {
    key: 'PAN',
    label: 'PAN CARD',
    title: 'Vendor / Entity PAN Card',
    category: 'Statutory & Tax Proof',
    desc: 'Permanent Account Number copy of entity or proprietor',
    patterns: ['pan', 'pan card', 'copy of pan allotment', 'pan copy'],
  },
  {
    key: 'GST',
    label: 'GST CERTIFICATE',
    title: 'GST Registration Certificate',
    category: 'Statutory & Tax Proof',
    desc: 'Form GST REG-06 or official GST registration certificate',
    patterns: ['gst', 'gst copy', 'gst certificate', 'gst registration', 'gstin'],
  },
  {
    key: 'BANK',
    label: 'BANK CANCELLED CHEQUE',
    title: 'Cancelled Cheque / Bank Proof',
    category: 'Banking & Financials',
    desc: 'Cancelled cheque or passbook/statement showing IFSC, Account Name & No.',
    patterns: ['bank', 'cheque', 'cancelled cheque', 'bank details', 'bank statement', 'bank proof'],
  },
  {
    key: 'AADHAR',
    label: 'AADHAR CARD',
    title: 'Aadhar Card (Authorized Signatory)',
    category: 'Identification',
    desc: 'Aadhar Card of Proprietor, Managing Partner, or Authorized Signatory',
    patterns: ['aadhar', 'aadhaar', 'uidai', 'aadhar card', 'adhar'],
  },
  {
    key: 'INCORPORATION',
    label: 'INCORPORATION / REGISTRATION CERTIFICATE',
    title: 'Certificate of Incorporation / Partnership Deed',
    category: 'Constitutional Documents',
    desc: 'Certificate of Incorporation, Partnership Deed, or Trade License',
    patterns: ['incorporation', 'certificate of incorporation', 'partnership deed', 'registration', 'coi'],
  },
  {
    key: 'MSME',
    label: 'MSME / UDYAM REGISTRATION',
    title: 'MSME / Udyam Certificate',
    category: 'Constitutional Documents',
    desc: 'Udyam Registration Certificate under MSMED Act (if applicable)',
    patterns: ['msme', 'udyam', 'udyam registration', 'msme certificate'],
  },
  {
    key: 'KYC_FORM',
    label: 'VENDOR KYC FORM / DECLARATION',
    title: 'Vendor Empanelment / KYC Form',
    category: 'Empanelment & Declaration',
    desc: 'Duly filled, signed & stamped Vendor Empanelment & KYC Format',
    patterns: ['kyc form', 'declaration', 'vendor kyc', 'kyc', 'empanelment form'],
  },
  {
    key: 'AUTHORITY',
    label: 'POWER OF ATTORNEY / AUTHORITY LETTER',
    title: 'Board Resolution / Authority Letter',
    category: 'Empanelment & Declaration',
    desc: 'Authority Letter or Board Resolution in favor of authorized signatory',
    patterns: ['power of attorney', 'authority letter', 'authorization', 'poa', 'board resolution'],
  },
  {
    key: 'ADDRESS_PROOF',
    label: 'UTILITY / TELEPHONE BILL',
    title: 'Registered Office Address Proof',
    category: 'Address Proof',
    desc: 'Recent Electricity or Telephone bill proof of registered operating office',
    patterns: ['telephone bill', 'utility bill', 'electricity bill', 'address proof'],
  },
  {
    key: 'IEC',
    label: 'IEC',
    title: 'IEC (Import Export Code)',
    category: 'Statutory & Tax Proof',
    desc: 'Mandatory DGFT code for cross-border freight forwarding / customs vendors',
    patterns: ['iec', 'import export code', 'ieccopy', 'iec copy'],
  },
];

export default function PublicVendorKycOnboarding() {
  const { token } = useParams();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [vendorData, setVendorData] = useState(null);
  const [activeTab, setActiveTab] = useState('branches'); // 'branches' | 'documents'

  // Branches state
  const [panNumber, setPanNumber] = useState('');
  const [branches, setBranches] = useState([]);
  const [savingBranches, setSavingBranches] = useState(false);

  // Documents state
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadingDocKey, setUploadingDocKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchVendorData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/public/vendor-kyc-onboarding/${token}`);
      if (!res.ok) {
        throw new Error('Invalid or expired Vendor KYC link');
      }
      const data = await res.json();
      setVendorData(data);
      setPanNumber(data.pan_number || '');
      setUploadedDocs(data.kyc_documents || []);

      if (data.branches && data.branches.length > 0) {
        setBranches(
          data.branches.map((b) => ({
            id: b.id,
            is_head_office: b.is_head_office,
            branch_nick_name: b.branch_nick_name || '',
            address_line_1: b.address_line_1 || '',
            address_line_2: b.address_line_2 || '',
            city: b.city || '',
            state: b.state || '',
            country: b.country || 'India',
            pincode: b.pincode || '',
            gst_number: b.gst_number || '',
            contact_person: b.contact_person || '',
            designation: b.designation || '',
            contact_numbers: b.contact_numbers && b.contact_numbers.length > 0 ? b.contact_numbers : [''],
            emails: b.emails && b.emails.length > 0 ? b.emails : [''],
          }))
        );
      } else {
        setBranches([
          {
            is_head_office: true,
            branch_nick_name: 'Head Office',
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            country: 'India',
            pincode: '',
            gst_number: '',
            contact_person: '',
            designation: '',
            contact_numbers: [''],
            emails: [''],
          },
        ]);
      }
    } catch (err) {
      toast({
        title: 'Error Loading KYC Link',
        description: err.message || 'Unable to load Vendor KYC details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token, toast]);

  useEffect(() => {
    fetchVendorData();
  }, [fetchVendorData]);

  // Branch handlers
  const handleAddBranch = () => {
    setBranches((prev) => [
      ...prev,
      {
        is_head_office: false,
        branch_nick_name: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        gst_number: '',
        contact_person: '',
        designation: '',
        contact_numbers: [''],
        emails: [''],
      },
    ]);
  };

  const handleRemoveBranch = (index) => {
    if (branches.length === 1) {
      toast({ title: 'At least one branch / office is required', variant: 'destructive' });
      return;
    }
    setBranches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBranchChange = (index, field, value) => {
    setBranches((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddArrayItem = (branchIdx, field) => {
    setBranches((prev) => {
      const updated = [...prev];
      updated[branchIdx][field] = [...updated[branchIdx][field], ''];
      return updated;
    });
  };

  const handleRemoveArrayItem = (branchIdx, field, itemIdx) => {
    setBranches((prev) => {
      const updated = [...prev];
      if (updated[branchIdx][field].length > 1) {
        updated[branchIdx][field] = updated[branchIdx][field].filter((_, i) => i !== itemIdx);
      }
      return updated;
    });
  };

  const handleArrayItemChange = (branchIdx, field, itemIdx, val) => {
    setBranches((prev) => {
      const updated = [...prev];
      const arr = [...updated[branchIdx][field]];
      arr[itemIdx] = val;
      updated[branchIdx][field] = arr;
      return updated;
    });
  };

  const handleSaveBranches = async () => {
    for (let i = 0; i < branches.length; i++) {
      const b = branches[i];
      if (!b.branch_nick_name.trim()) {
        toast({ title: 'Validation', description: `Branch #${i + 1}: Name is required`, variant: 'destructive' });
        return;
      }
      if (!b.address_line_1.trim()) {
        toast({ title: 'Validation', description: `Branch #${i + 1}: Address Line 1 is required`, variant: 'destructive' });
        return;
      }
      if (!b.contact_person.trim()) {
        toast({ title: 'Validation', description: `Branch #${i + 1}: Contact Person is required`, variant: 'destructive' });
        return;
      }
      if (!b.contact_numbers.some((n) => n.trim())) {
        toast({ title: 'Validation', description: `Branch #${i + 1}: At least one phone number is required`, variant: 'destructive' });
        return;
      }
    }

    try {
      setSavingBranches(true);
      const res = await fetch(`${backendUrl}/api/public/vendor-kyc-onboarding/${token}/branches`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pan_number: panNumber,
          branches: branches.map((b) => ({
            ...b,
            contact_numbers: b.contact_numbers.filter((c) => c && c.trim()),
            emails: b.emails.filter((e) => e && e.trim()),
          })),
        }),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to save branches');
      }

      toast({ title: 'Success', description: 'Branches & contact details saved successfully' });
      setActiveTab('documents');
    } catch (err) {
      toast({ title: 'Error Saving Branches', description: err.message, variant: 'destructive' });
    } finally {
      setSavingBranches(false);
    }
  };

  // Document matching
  const getUploadedDoc = (docConfig) => {
    return uploadedDocs.find((d) => {
      const name = (d.document_name || '').toLowerCase().trim();
      return docConfig.patterns.some((p) => name.includes(p.toLowerCase()));
    });
  };

  const handleFileUpload = async (docConfig, file) => {
    if (!file) return;
    try {
      setUploadingDocKey(docConfig.key);
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(
        `${backendUrl}/api/public/vendor-kyc-onboarding/${token}/documents?document_name=${encodeURIComponent(
          docConfig.label
        )}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to upload document');
      }

      const result = await res.json();
      setUploadedDocs((prev) => {
        const filtered = prev.filter(
          (d) => !docConfig.patterns.some((p) => (d.document_name || '').toLowerCase().includes(p.toLowerCase()))
        );
        return [...filtered, result.document];
      });

      toast({
        title: 'Document Uploaded',
        description: `${docConfig.title} uploaded successfully`,
      });
    } catch (err) {
      toast({ title: 'Upload Failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploadingDocKey(null);
    }
  };

  const handleDeleteDoc = async (docId, docTitle) => {
    try {
      const res = await fetch(
        `${backendUrl}/api/public/vendor-kyc-onboarding/${token}/documents/${docId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to remove document');
      }
      setUploadedDocs((prev) => prev.filter((d) => d.id !== docId));
      toast({ title: 'Document Removed', description: `${docTitle} was removed` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleSubmitKyc = async () => {
    if (uploadedDocs.length === 0) {
      toast({
        title: 'No Documents Uploaded',
        description: 'Please upload the applicable KYC documents for your vendor entity before submitting.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${backendUrl}/api/public/vendor-kyc-onboarding/${token}/submit`, {
        method: 'POST',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to submit KYC');
      }

      toast({
        title: 'KYC Submitted Successfully!',
        description: 'Your details & documents have been securely locked for verification.',
      });
      fetchVendorData();
    } catch (err) {
      toast({ title: 'Submission Failed', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <Loader2 className="w-10 h-10 animate-spin text-accent-blue mb-4" />
        <h2 className="text-lg font-semibold text-slate-800 font-manrope">Loading Vendor KYC Portal...</h2>
        <p className="text-sm text-slate-500 font-ibm mt-1">Verifying secure onboarding link</p>
      </div>
    );
  }

  if (!vendorData) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-200">
          <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 font-manrope mb-2">Invalid or Expired Link</h2>
          <p className="text-sm text-slate-600 font-ibm">
            This vendor onboarding link is invalid or has expired. Please contact the logistics procurement team for a new link.
          </p>
        </div>
      </div>
    );
  }

  const isLocked = vendorData.kyc_locked && !vendorData.rekyc_requested;
  const isRekyc = vendorData.rekyc_requested;
  const completedCount = uploadedDocs.length;
  const totalCount = COMMON_VENDOR_KYC_DOCS.length;
  const progressPercent = Math.min(100, Math.round((completedCount / totalCount) * 100));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100/60 to-blue-50/30 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Top Header Card */}
        <Card className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="bg-primary-blue p-6 sm:p-8 text-white relative">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-xs font-semibold uppercase tracking-wider text-white/90">
                  <Landmark className="w-3.5 h-3.5" /> Vendor Onboarding Portal
                </div>
                <h1 className="text-2xl sm:text-3xl font-extrabold font-manrope tracking-tight">
                  Vendor Empanelment & KYC Submission
                </h1>
                <p className="text-sm text-white/80 font-ibm">
                  Review verified legal profile, update branch offices, and upload statutory KYC records.
                </p>
              </div>

              {isLocked ? (
                <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 px-4 py-2 rounded-xl text-emerald-200 text-xs font-semibold shrink-0">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <div>
                    <div className="font-bold">KYC Submitted &amp; Locked</div>
                    <div className="text-[10px] text-emerald-300/80">Under procurement verification</div>
                  </div>
                </div>
              ) : isRekyc ? (
                <div className="flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 px-4 py-2 rounded-xl text-amber-200 text-xs font-semibold shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  <div>
                    <div className="font-bold">Re-KYC Requested</div>
                    <div className="text-[10px] text-amber-300/80">Edits unlocked for correction</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-blue-500/20 border border-blue-400/40 px-4 py-2 rounded-xl text-blue-200 text-xs font-semibold shrink-0">
                  <ShieldCheck className="w-5 h-5 text-blue-400" />
                  <div>
                    <div className="font-bold">KYC In Progress</div>
                    <div className="text-[10px] text-blue-300/80">Fill &amp; submit for approval</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Re-KYC Alert Banner */}
          {isRekyc && (
            <div className="p-4 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Attention Required:</span> Admin requested updated KYC documents:
                <p className="mt-1 font-mono text-xs bg-white/70 p-2 rounded border border-amber-300">
                  {vendorData.rekyc_remarks || 'Please update your KYC documents and submit again.'}
                </p>
              </div>
            </div>
          )}

          {/* Locked Notice Banner */}
          {isLocked && (
            <div className="p-4 bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-sm flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Documents Successfully Submitted:</span> Your KYC records are submitted and locked for verification. Contact logistics procurement if you need to modify details.
              </div>
            </div>
          )}

          {/* Fixed Business Legal Name Banner */}
          <div className="p-6 bg-slate-50/70 border-b border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <Lock className="w-3.5 h-3.5 text-slate-400" /> Verified Vendor Legal Name (Locked)
              </div>
              <div className="text-xl font-bold text-slate-900 font-manrope flex items-center gap-2">
                <Building2 className="w-5 h-5 text-accent-blue" />
                {vendorData.business_name}
              </div>
              <p className="text-xs text-slate-500 font-ibm">
                Legal entity name is configured by admin and cannot be changed.
              </p>
            </div>

            <div className="w-full md:w-auto">
              <Label className="text-xs font-semibold text-slate-600 block mb-1">Permanent Account Number (PAN)</Label>
              <Input
                value={panNumber}
                onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                disabled={isLocked || !!vendorData.pan_number}
                placeholder="ABCDE1234F"
                maxLength={10}
                className="w-full md:w-48 font-mono uppercase font-bold text-sm bg-white"
              />
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex border-b border-slate-200 px-6 bg-white">
            <button
              onClick={() => setActiveTab('branches')}
              className={`py-4 px-4 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${
                activeTab === 'branches'
                  ? 'border-accent-blue text-accent-blue'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <MapPin className="w-4 h-4" />
              1. Branch &amp; Operating Offices ({branches.length})
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-4 px-4 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${
                activeTab === 'documents'
                  ? 'border-accent-blue text-accent-blue'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <FileText className="w-4 h-4" />
              2. Statutory KYC Documents ({completedCount} Attached)
            </button>
          </div>
        </Card>

        {/* Tab 1: Branches & Contact Details */}
        {activeTab === 'branches' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 font-manrope">Operating Branches &amp; Key Contacts</h2>
                <p className="text-xs text-slate-500 font-ibm">
                  Add branch locations, contact person, phone numbers, and operational emails.
                </p>
              </div>
              {!isLocked && (
                <Button
                  onClick={handleAddBranch}
                  type="button"
                  variant="outline"
                  className="border-accent-blue text-accent-blue hover:bg-accent-blue/10 font-ibm text-xs font-semibold"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Add Another Branch
                </Button>
              )}
            </div>

            {branches.map((branch, bIdx) => (
              <Card key={bIdx} className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
                <div className="bg-slate-50 px-6 py-3.5 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-full bg-accent-blue text-white flex items-center justify-center text-xs font-bold font-ibm">
                      {bIdx + 1}
                    </span>
                    <span className="font-bold text-sm text-slate-800 font-manrope">
                      {branch.branch_nick_name || `Branch #${bIdx + 1}`}
                    </span>
                    {branch.is_head_office && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] font-bold uppercase">
                        Head Office
                      </Badge>
                    )}
                  </div>
                  {!isLocked && branches.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveBranch(bIdx)}
                      className="text-red-600 hover:bg-red-50 text-xs h-8"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Branch Nick Name *</Label>
                      <Input
                        value={branch.branch_nick_name}
                        onChange={(e) => handleBranchChange(bIdx, 'branch_nick_name', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. Mumbai Logistics Hub"
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs font-semibold text-slate-700">Address Line 1 *</Label>
                      <Input
                        value={branch.address_line_1}
                        onChange={(e) => handleBranchChange(bIdx, 'address_line_1', e.target.value)}
                        disabled={isLocked}
                        placeholder="Street / Building / Plot No."
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Address Line 2</Label>
                      <Input
                        value={branch.address_line_2}
                        onChange={(e) => handleBranchChange(bIdx, 'address_line_2', e.target.value)}
                        disabled={isLocked}
                        placeholder="Landmark / Area"
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">City</Label>
                      <Input
                        value={branch.city}
                        onChange={(e) => handleBranchChange(bIdx, 'city', e.target.value)}
                        disabled={isLocked}
                        placeholder="City"
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">State</Label>
                      <Input
                        value={branch.state}
                        onChange={(e) => handleBranchChange(bIdx, 'state', e.target.value)}
                        disabled={isLocked}
                        placeholder="State"
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Pincode</Label>
                      <Input
                        value={branch.pincode}
                        onChange={(e) => handleBranchChange(bIdx, 'pincode', e.target.value)}
                        disabled={isLocked}
                        placeholder="400001"
                        maxLength={6}
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">GST Number</Label>
                      <Input
                        value={branch.gst_number}
                        onChange={(e) => handleBranchChange(bIdx, 'gst_number', e.target.value.toUpperCase())}
                        disabled={isLocked}
                        placeholder="27AABCU9603R1ZM"
                        className="mt-1 text-sm font-mono uppercase"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Contact Person *</Label>
                      <Input
                        value={branch.contact_person}
                        onChange={(e) => handleBranchChange(bIdx, 'contact_person', e.target.value)}
                        disabled={isLocked}
                        placeholder="Full Name"
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Designation</Label>
                      <Input
                        value={branch.designation}
                        onChange={(e) => handleBranchChange(bIdx, 'designation', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. Operations Manager / Director"
                        className="mt-1 text-sm font-ibm"
                      />
                    </div>

                    {/* Phone numbers */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5 text-slate-500" /> Phone Numbers *
                        </Label>
                        {!isLocked && (
                          <button
                            type="button"
                            onClick={() => handleAddArrayItem(bIdx, 'contact_numbers')}
                            className="text-[11px] text-accent-blue font-semibold hover:underline"
                          >
                            + Add Phone
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {branch.contact_numbers.map((phone, pIdx) => (
                          <div key={pIdx} className="flex gap-2">
                            <Input
                              value={phone}
                              onChange={(e) => handleArrayItemChange(bIdx, 'contact_numbers', pIdx, e.target.value)}
                              disabled={isLocked}
                              placeholder="+91 98765 43210"
                              className="text-sm font-ibm"
                            />
                            {!isLocked && branch.contact_numbers.length > 1 && (
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                onClick={() => handleRemoveArrayItem(bIdx, 'contact_numbers', pIdx)}
                                className="shrink-0 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Emails */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5 text-slate-500" /> Operational Emails
                        </Label>
                        {!isLocked && (
                          <button
                            type="button"
                            onClick={() => handleAddArrayItem(bIdx, 'emails')}
                            className="text-[11px] text-accent-blue font-semibold hover:underline"
                          >
                            + Add Email
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {branch.emails.map((email, eIdx) => (
                          <div key={eIdx} className="flex gap-2">
                            <Input
                              type="email"
                              value={email}
                              onChange={(e) => handleArrayItemChange(bIdx, 'emails', eIdx, e.target.value)}
                              disabled={isLocked}
                              placeholder="dispatch@vendor.com"
                              className="text-sm font-ibm"
                            />
                            {!isLocked && branch.emails.length > 1 && (
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                onClick={() => handleRemoveArrayItem(bIdx, 'emails', eIdx)}
                                className="shrink-0 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                onClick={handleSaveBranches}
                disabled={savingBranches || isLocked}
                className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold px-6 h-11 rounded-lg"
              >
                {savingBranches ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving Branches...
                  </>
                ) : (
                  <>
                    Save &amp; Continue to Documents <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Tab 2: Statutory Documents */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            {/* Progress Card */}
            <Card className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
                <span>Attached Documents: {completedCount} applicable documents uploaded</span>
                <span className="text-slate-900 font-bold">{progressPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                <div
                  className="bg-accent-blue h-full transition-all duration-300 rounded-full"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </Card>

            {/* Entity Notice Banner */}
            <div className="p-3.5 bg-blue-50/80 border border-blue-200 rounded-xl text-xs text-blue-900 flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Upload whatever applies to your company:</span> Transporters, Fleet Owners, Brokers, and Logistics Providers (Proprietorship, Partnership, Pvt Ltd) require different documents. Upload whichever documents apply to your entity structure. Documents not applicable to you can simply be skipped.
              </div>
            </div>

            {/* Document Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {COMMON_VENDOR_KYC_DOCS.map((docConfig) => {
                const uploaded = getUploadedDoc(docConfig);
                const isUploading = uploadingDocKey === docConfig.key;

                return (
                  <div
                    key={docConfig.key}
                    className={`rounded-xl border p-5 flex flex-col justify-between transition-all ${
                      uploaded
                        ? 'bg-emerald-50/40 border-emerald-300 shadow-sm'
                        : 'bg-white border-slate-200/90 hover:border-blue-400 hover:shadow-sm'
                    }`}
                  >
                    <div>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="font-bold text-sm text-slate-900 font-manrope flex items-center gap-2">
                          {uploaded ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          ) : (
                            <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                          )}
                          {docConfig.title}
                        </div>
                        {uploaded ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase">
                            ✓ Attached
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase">
                            As Applicable
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-500 font-ibm mb-3">{docConfig.desc}</p>
                    </div>

                    <div className="pt-3 border-t border-slate-100">
                      {uploaded ? (
                        <div className="flex items-center justify-between bg-white/80 p-2.5 rounded-lg border border-emerald-200">
                          <div className="min-w-0 pr-2">
                            <p className="text-xs font-semibold text-slate-800 truncate font-ibm">
                              {uploaded.file_name}
                            </p>
                            <p className="text-[10px] text-slate-500 font-ibm">Uploaded</p>
                          </div>
                          {!isLocked && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteDoc(uploaded.id, docConfig.title)}
                              className="text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            id={`file-input-${docConfig.key}`}
                            className="hidden"
                            disabled={isLocked || isUploading}
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(docConfig, e.target.files[0]);
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isLocked || isUploading}
                            onClick={() => {
                              document.getElementById(`file-input-${docConfig.key}`)?.click();
                            }}
                            className="w-full text-xs font-semibold bg-blue-50/60 hover:bg-blue-100/80 text-blue-900 border-blue-200 h-9"
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-3.5 h-3.5 mr-1.5" /> Attach Document
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom Actions Card */}
            <Card className="bg-white rounded-xl p-6 border border-slate-200/80 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900 font-manrope">Ready to Submit Vendor Onboarding?</h3>
                <p className="text-xs text-slate-500 font-ibm mt-0.5">
                  Submitting locks your profile and documents for verification by logistics procurement.
                </p>
              </div>

              <Button
                type="button"
                onClick={handleSubmitKyc}
                disabled={isLocked || completedCount === 0 || submitting}
                className="bg-accent-green hover:bg-accent-green/90 text-white font-semibold font-ibm px-6 h-11 rounded-lg shrink-0"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...
                  </>
                ) : isLocked ? (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" /> KYC Submitted &amp; Locked
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Submit KYC for Verification
                  </>
                )}
              </Button>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
